/**
 * Free-text search and filtering, over the local replica (FR-3.2, FR-3.3).
 *
 * Both run entirely on the client against the in-memory manifest. That is
 * assumption A7 being cashed in: because one user's whole catalog fits in memory,
 * there is no search service, no aggregation pipeline and no secondary index --
 * and `NFR-PERF-3`'s 300 ms budget is met by not making a network call at all.
 *
 * In `shared` rather than the client because it is a property of the domain, not
 * of a view, and because the export in Epic 11 will need to apply the same filter
 * server-side to decide what to include.
 */
import { resolveTagId } from './merge.js';
import { fieldValue, isArchived, isDeleted, isFavorite } from './merge.js';
import { ancestorsOf, buildTree, flattenTree, withDescendants } from './taxonomy.js';
import { hasColor, splitColors } from './colors.js';
/**
 * Splits text into searchable tokens.
 *
 * Unicode-aware on purpose: `\w` would split "café" into "caf" and leave the
 * accented character unsearchable, and would discard CJK entirely. Case and
 * composition are normalized so that a name typed on one keyboard matches the
 * same name typed on another.
 */
export function tokenize(text) {
    return text
        .normalize('NFC')
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((token) => token !== '');
}
/** The fields FR-3.2 names as searchable. */
const TEXT_FIELDS = ['name', 'notes', 'brand', 'model', 'material'];
/**
 * An inverted index from token to item.
 *
 * Maintained incrementally rather than rebuilt. A rebuild is O(catalog) and a
 * keystroke-driven UI writes often, so rebuilding on every edit would make typing
 * in the capture form progressively slower as the manifest grew -- the one thing
 * `NFR-PERF-1` cannot tolerate.
 *
 * `tokensByItem` exists purely so an item can be removed from its old tokens when
 * it changes. Without it, renaming an item would leave it findable under its
 * previous name forever.
 */
export class SearchIndex {
    byToken = new Map();
    tokensByItem = new Map();
    get size() {
        return this.byToken.size;
    }
    /** Re-indexes one item, replacing whatever was indexed for it before. */
    index(item, extraTerms = []) {
        this.remove(item.itemId);
        const tokens = new Set();
        for (const field of TEXT_FIELDS) {
            const value = fieldValue(item, field);
            if (typeof value === 'string')
                for (const token of tokenize(value))
                    tokens.add(token);
        }
        // Custom field values are searchable too, which is part of why they are rows
        // rather than an opaque blob (glossary, CUSTOM_FIELD_VALUE).
        for (const cell of item.customFields.values()) {
            if (typeof cell.v === 'string')
                for (const token of tokenize(cell.v))
                    tokens.add(token);
            else if (typeof cell.v === 'number')
                tokens.add(String(cell.v));
        }
        // Terms that live on other records -- tag names, and the names of the
        // category and location and their ancestors -- supplied by the caller.
        for (const term of extraTerms)
            for (const token of tokenize(term))
                tokens.add(token);
        if (tokens.size === 0)
            return;
        this.tokensByItem.set(item.itemId, tokens);
        for (const token of tokens) {
            const items = this.byToken.get(token);
            if (items)
                items.add(item.itemId);
            else
                this.byToken.set(token, new Set([item.itemId]));
        }
    }
    remove(itemId) {
        const tokens = this.tokensByItem.get(itemId);
        if (tokens === undefined)
            return;
        for (const token of tokens) {
            const items = this.byToken.get(token);
            if (items === undefined)
                continue;
            items.delete(itemId);
            // Dropping the empty set keeps the vocabulary from growing without bound as
            // items are renamed, which is what a prefix scan costs per query.
            if (items.size === 0)
                this.byToken.delete(token);
        }
        this.tokensByItem.delete(itemId);
    }
    /** Rebuilds from scratch, for a fresh load or after a bootstrap. */
    rebuild(manifest) {
        this.byToken.clear();
        this.tokensByItem.clear();
        for (const item of manifest.items.values()) {
            this.index(item, searchTermsFor(manifest, item));
        }
    }
    /**
     * Item ids matching every term, each matched as a prefix.
     *
     * Prefix rather than exact, because someone typing "mer" expects to find a
     * merino tee before finishing the word. Terms are combined with AND: adding a
     * word should narrow a result set, which is what people expect of a search box
     * and the only behavior that makes a long query useful.
     *
     * Prefix matching scans the vocabulary rather than using a trie. At one user's
     * scale the vocabulary is thousands of tokens, and a scan of that is far below
     * the `NFR-PERF-3` budget; a trie would be faster and is not yet worth the code.
     */
    search(query) {
        const terms = tokenize(query);
        if (terms.length === 0)
            return null;
        let matched = null;
        for (const term of terms) {
            const forTerm = new Set();
            const exact = this.byToken.get(term);
            if (exact !== undefined)
                for (const id of exact)
                    forTerm.add(id);
            for (const [token, items] of this.byToken) {
                if (token !== term && token.startsWith(term))
                    for (const id of items)
                        forTerm.add(id);
            }
            matched = matched === null ? forTerm : intersect(matched, forTerm);
            // Nothing can be recovered by looking at further terms.
            if (matched.size === 0)
                return matched;
        }
        return matched;
    }
}
const intersect = (a, b) => {
    const [small, large] = a.size <= b.size ? [a, b] : [b, a];
    const out = new Set();
    for (const value of small)
        if (large.has(value))
            out.add(value);
    return out;
};
/**
 * Display names of the tags applied to an item.
 *
 * Resolved through any merge, so an item tagged with a tag that was later merged
 * is findable under the surviving tag's name rather than a name nothing shows.
 */
export function tagNamesFor(manifest, item) {
    const names = [];
    for (const [tagId, applied] of item.tags) {
        if (!applied.present)
            continue;
        const tag = manifest.tags.get(resolveTagId(manifest, tagId));
        if (tag?.lifecycle?.present === false)
            continue;
        if (tag?.displayName?.v !== undefined)
            names.push(tag.displayName.v);
    }
    return names;
}
/**
 * Every term an item should be findable by that does not live on the item.
 *
 * Tag names, plus the names of its category and location *and all their
 * ancestors*. The ancestors matter: searching "Footwear" has to find a pair of
 * running shoes filed under "Footwear / Running shoes", because the whole point
 * of nesting is that the parent describes the child. Indexing only the leaf
 * would make a tree less useful than a flat list.
 *
 * Supplied by the caller rather than read inside `index` because these live on
 * other records. An item carries an id; the word a person types is somewhere
 * else, and the index must not need the whole manifest to add one item.
 */
export function searchTermsFor(manifest, item) {
    const terms = tagNamesFor(manifest, item);
    for (const [field, records] of [
        ['categoryId', manifest.categories],
        ['locationId', manifest.locations],
    ]) {
        const id = fieldValue(item, field);
        if (typeof id !== 'string' || id === '')
            continue;
        // `ancestorsOf` is cycle-guarded and stops at a missing or deleted ancestor,
        // which two devices editing the same branch offline can produce.
        for (const ancestorId of ancestorsOf(records, id)) {
            const name = records.get(ancestorId)?.fields.get('name')?.v;
            if (typeof name === 'string' && name !== '')
                terms.push(name);
        }
    }
    return terms;
}
/**
 * FR-3.3's filters, any combination.
 *
 * `intention: 'unreviewed'` selects items with no intention record, which the
 * glossary is explicit is the absence of a verdict rather than a verdict of its
 * own -- so it cannot be expressed as one of the four values.
 */
/**
 * Every field admits an explicit `undefined`, not merely absence.
 *
 * Under `exactOptionalPropertyTypes` those are different types, and clearing a
 * filter is genuinely the first: a UI setting `{ brand: undefined }` is saying
 * "no brand filter", which is a state the type should be able to express rather
 * than something each call site casts around.
 */
/**
 * Asks for the items filed in no category at all (FR-3.3).
 *
 * A sentinel in `categoryId` rather than a flag of its own, because "which
 * category" has exactly one answer per query and a second field would let a
 * caller ask for two: `{ categoryId: clothing, unfiled: true }` has no meaning.
 * `intention: 'unreviewed'` is the same shape for the same reason.
 *
 * Not a well-formed id, so it cannot collide with a real one.
 */
export const UNFILED = 'unfiled';
const matchesVisibility = (item, visibility) => {
    switch (visibility) {
        case 'deleted':
            return isDeleted(item);
        case 'archived':
            return isArchived(item) && !isDeleted(item);
        case 'all':
            return true;
        default:
            return !isDeleted(item) && !isArchived(item);
    }
};
const equals = (item, field, want) => 
// Case-insensitive, because these are free text: someone who typed "Icebreaker"
// on one device and "icebreaker" on another means the same brand.
String(fieldValue(item, field) ?? '').toLowerCase() === want.toLowerCase();
/**
 * Items matching the filter, in a stable order.
 *
 * The text term is applied first when an index is given, because it is the most
 * selective and reduces what every other predicate has to look at.
 *
 * A category or location filter includes everything nested beneath it: asking
 * for "Clothing" means the shirts filed under "Clothing / Tops", and asking for
 * "House" means the box in "House / Loft". That widening used to live in the
 * catalog screen, which meant the catalog and every other surface gave
 * different answers to the same filter -- the kind of disagreement NFR-FUNC-1
 * exists to forbid. It belongs here, where there is one definition of it.
 */
export function filterItems(manifest, filter, index) {
    const textMatches = filter.text !== undefined && index !== undefined ? index.search(filter.text) : null;
    // Built once per call rather than once per item: the tree is small, but this
    // runs on every keystroke and per-item would make it quadratic.
    const wantedCategories = filter.categoryId === undefined || filter.categoryId === UNFILED
        ? null
        : withDescendants(buildTree(manifest.categories), filter.categoryId);
    /*
     * The live categories, for the `UNFILED` case.
     *
     * Membership of the tree rather than "the field is empty", so that an item
     * left pointing at a deleted category counts as unfiled. It is what the rest
     * of the app already shows -- the row prints no category for it and sorting by
     * category files it with the blanks -- and an item nobody can reach through
     * the category filter is exactly the one this filter exists to find.
     */
    const filedSomewhere = filter.categoryId === UNFILED
        ? new Set(flattenTree(buildTree(manifest.categories)).map((node) => node.id))
        : null;
    const wantedLocations = filter.locationId === undefined
        ? null
        : withDescendants(buildTree(manifest.locations), filter.locationId);
    const candidates = textMatches === null
        ? [...manifest.items.values()]
        : [...textMatches].flatMap((id) => {
            const item = manifest.items.get(id);
            return item === undefined ? [] : [item];
        });
    const matched = candidates.filter((item) => {
        if (!matchesVisibility(item, filter.visibility ?? 'active'))
            return false;
        if (filedSomewhere !== null) {
            const categoryId = fieldValue(item, 'categoryId');
            if (typeof categoryId === 'string' && filedSomewhere.has(categoryId))
                return false;
        }
        if (wantedCategories !== null) {
            const categoryId = fieldValue(item, 'categoryId');
            // A filtered id that resolves to no live node matches nothing rather than
            // everything: the category was deleted elsewhere, and the honest answer to
            // "show me that category" is that there is nothing in it.
            if (typeof categoryId !== 'string' || !wantedCategories.has(categoryId))
                return false;
        }
        if (wantedLocations !== null) {
            const locationId = fieldValue(item, 'locationId');
            if (typeof locationId !== 'string' || !wantedLocations.has(locationId))
                return false;
        }
        if (filter.tagId !== undefined) {
            const resolved = resolveTagId(manifest, filter.tagId);
            const applied = [...item.tags.entries()].some(([tagId, decision]) => decision.present && resolveTagId(manifest, tagId) === resolved);
            if (!applied)
                return false;
        }
        if (filter.favorite === true && !isFavorite(item))
            return false;
        if (filter.brand !== undefined && !equals(item, 'brand', filter.brand))
            return false;
        /*
         * Membership, not equality. A colour field holds a set (`colors.ts`), so
         * asking for Orange has to find the thing that is Orange and White --
         * which is the whole reason that field is read as a set.
         */
        if (filter.color !== undefined && !hasColor(item, filter.color))
            return false;
        if (filter.condition !== undefined && !equals(item, 'condition', filter.condition))
            return false;
        if (filter.intention !== undefined) {
            const current = item.currentIntention;
            if (filter.intention === 'unreviewed') {
                if (current !== null)
                    return false;
            }
            else if (current !== filter.intention) {
                return false;
            }
        }
        if (filter.acquiredFrom !== undefined || filter.acquiredTo !== undefined) {
            const acquired = fieldValue(item, 'acquiredOn');
            // An item with no acquisition date is outside any range, rather than inside
            // every one: a date filter asks about items whose date is known.
            if (typeof acquired !== 'string')
                return false;
            // `YYYY-MM-DD` compares correctly as a string, which is part of why dates
            // are stored that way (NFR-FUNC-4).
            if (filter.acquiredFrom !== undefined && acquired < filter.acquiredFrom)
                return false;
            if (filter.acquiredTo !== undefined && acquired > filter.acquiredTo)
                return false;
        }
        return true;
    });
    return matched.sort((a, b) => String(fieldValue(a, 'name') ?? '').localeCompare(String(fieldValue(b, 'name') ?? '')));
}
/**
 * The distinct values present for the free-text descriptors.
 *
 * FR-3.3 filters by brand, color and condition, none of which has a fixed
 * vocabulary -- so the options are whatever the manifest actually contains. This
 * is also what makes a filter over `condition` workable without inventing a scale
 * nobody agreed to.
 *
 * `sizes` is here for the capture form's suggestions (FR-2.11) rather than for a
 * filter. It is derived the same way and belongs beside the others: a second
 * function collecting distinct values of a descriptor is a second answer to one
 * question waiting to disagree.
 */
export function facets(manifest) {
    /*
     * A colour field holds a set and the others hold one value, so the options
     * for colour are its members -- otherwise "Orange/White" is offered as a
     * third colour that matches neither Orange nor White when picked.
     */
    const valuesOf = (item, field) => {
        if (field === 'color')
            return splitColors(fieldValue(item, field));
        const value = fieldValue(item, field);
        return typeof value === 'string' && value.trim() !== '' ? [value.trim()] : [];
    };
    const collect = (field) => {
        const seen = new Map();
        for (const item of manifest.items.values()) {
            if (isDeleted(item))
                continue;
            for (const value of valuesOf(item, field)) {
                // Keyed case-insensitively, but the casing the user typed is what is shown.
                seen.set(value.toLowerCase(), value);
            }
        }
        return [...seen.values()].sort((a, b) => a.localeCompare(b));
    };
    return {
        brands: collect('brand'),
        colors: collect('color'),
        conditions: collect('condition'),
        sizes: collect('size'),
    };
}
//# sourceMappingURL=search.js.map